<template>
  <v-card flat tile class="budget-landing">
    <v-card-text>
      <v-container class="pa-0">
        <v-row class="bordered-row pb-0 pt-7 px-1">
          <span class="px-1 mt-2">I want to show</span>
          <span>
            <v-combobox
              :items="properties"
              v-model="selectedProperties"
              @input="dirtify"
              :filter="returnHeadersAndFilter"
              placeholder="Select"
              multiple
              dense
              outlined
              small-chips
            >
              <template v-slot:prepend-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-checkbox
                        label="View All"
                        @change="toggleSelectAllProperties"
                        :input-value="allPropertiesSelected"
                      ></v-checkbox>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>
          </span>
          <span class="px-1 mt-2">for </span>
          <v-select
            :items="yearOptions"
            v-model="selectedYearOption"
            @input="dirtify"
            placeholder="Select"
            dense
            outlined
          ></v-select>
          <span class="px-1 mt-2">and use</span>
          <v-select
            :items="useOptions"
            v-model="selectedUseOption"
            @input="dirtify"
            dense
            outlined
          ></v-select>
          <v-btn
            class="mx-2 white--text"
            color="resBlue darken-1"
            :disabled="!canFilter"
            @click="apply"
            >{{ filterBtnText }}</v-btn
          >
          <v-divider vertical></v-divider>

          <v-menu left offset-y close-on-click>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" rounded icon
                ><v-icon color="resBlue darken-1"
                  >mdi-dots-horizontal-circle</v-icon
                ></v-btn
              >
            </template>
            <v-list>
              <v-list-item @click="doExport" :disabled="!canExport"
                ><v-list-item-title>Export</v-list-item-title></v-list-item
              >
              <v-divider></v-divider>
              <v-list-item @click="clear"
                ><v-list-item-title>Clear</v-list-item-title></v-list-item
              >
            </v-list>
          </v-menu>
        </v-row>
        <v-row class="pb-0 px=1">
          <v-progress-linear
            v-if="currentLoading"
            class="mt-5"
            indeterminate
          ></v-progress-linear>
          <div v-if="!currentLoading && dataReady" class="mt-5 scrollable">
            <template v-for="(section, index) in table.sections">
              <div
                class="group-header"
                :style="groupHeaderStyle"
                :key="index"
                v-if="section.groupTitle"
                @click="toggleSection(section)"
              >
                <v-icon class="icon">{{
                  section.hide ? "mdi-chevron-down" : "mdi-chevron-right"
                }}</v-icon>
                {{ section.groupTitle }}
              </div>
              <table v-show="!section.hide" :key="index + 'section'">
                <tbody>
                  <template
                    v-for="field in getVisibleFieldsForSection(section)"
                  >
                    <tr :key="field.field">
                      <td
                        class="fieldName"
                        :class="field.indent ? 'indentation' : ''"
                      >
                        {{ field.name }}
                      </td>
                      <template
                        v-for="(
                          propertySettings, propIndex
                        ) in propertySettingsData"
                      >
                        <td :key="propIndex + 'prop'" :class="field.class">
                          {{
                            field.isArray
                              ? propertySettings[field.field][0]
                              : propertySettings[field.field]
                          }}
                        </td>
                      </template>
                    </tr>
                    <template v-if="field.isArray">
                      <tr
                        :key="valueIndex + 'v'"
                        v-for="(
                          value, valueIndex
                        ) in getMaxLengthArrayForArrayField(field.field)"
                      >
                        <td v-if="valueIndex > 0"></td>
                        <template v-if="valueIndex > 0">
                          <td
                            :key="arrayPropIndex + 'ap'"
                            v-for="(
                              arrayPropertySettings, arrayPropIndex
                            ) in propertySettingsData"
                          >
                            {{ arrayPropertySettings[field.field][valueIndex] }}
                          </td>
                        </template>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </template>
          </div>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import propertyService from "@/services/property";
import budgetService from "@/services/budget";

import { mapActions } from "vuex";
import { mapGetters } from "vuex";

import Vue from "vue";

export default {
  name: "SettingsSummary",
  components: {},
  data: function () {
    return {
      dirty: false,
      properties: [],
      selectedProperties: [],
      allPropertiesSelected: false,
      yearOptions: [],
      selectedYearOption: 0,
      useOptions: [
        {
          text: "the most recent version",
          value: 1,
        },
        {
          text: "only the published version",
          value: 2,
        },
        {
          text: "all versions",
          value: 3,
        },
        {
          text: "the most recent reforecast version",
          value: 4,
        },
      ],
      selectedUseOption: 1,
      propertySettingsData: [],
      colDefs: [],
      table: {
        sections: [
          {
            groupTitle: "",
            hide: false,
            fields: [
              {
                name: "Property",
                field: "propertyName",
                class: "bold",
              },
            ],
          },
          {
            groupTitle: "General Settings",
            fields: [
              {
                name: "Year",
                field: "year",
              },
              {
                name: "Start Month",
                field: "startMonth",
              },
              {
                name: "Accounting Method",
                field: "accountingMethod",
              },
              {
                name: "Version Description",
                field: "versionDescription",
              },
              {
                name: "Version Identifier",
                field: "versionIdentifier",
              },
              {
                name: "Status",
                field: "status",
              },
              {
                name: "Template",
                field: "template",
              },
              {
                name: "Estimate Future Historicals",
                field: "estimateFutureHistoricals",
              },
              {
                name: "Starting In",
                field: "startingIn",
                indent: true,
              },
              {
                name: "Approval Workflow",
                field: "approvalWorkflow",
              },
            ],
          },
          {
            groupTitle: "Lease Calculations",
            fields: [
              {
                name: "New Lease Calculations",
                field: "newLeaseCalculations",
              },
              {
                name: "New Lease Percentage",
                field: "newLeasePercentage",
                indent: true,
              },
              {
                name: "New Lease Concession",
                field: "newLeaseConcession",
                indent: true,
              },
              {
                name: "Renewal Pricing Calculations",
                field: "renewalPricingCalculations",
              },
              {
                name: "Renewal Pricing Percentage",
                field: "renewalPricingPercentage",
                indent: true,
              },
              {
                name: "Renewal Pricing Concession",
                field: "renewalPricingConcession",
                indent: true,
              },
            ],
          },
          {
            groupTitle: "Concessions & Bad Debt",
            fields: [
              {
                name: "Additional Concessions",
                field: "additionalConcessions",
                isArray: true,
              },
              {
                name: "Bad Debt Calculation",
                field: "badDebtCalculation",
              },
              {
                name: "Bad Debt Percentage",
                field: "badDebtPercentage",
                indent: true,
              },
              {
                name: "Bad Debt GL Account",
                field: "badDebtGLAccount",
                indent: true,
              },
              {
                name: "Bad Debt Transaction Category",
                field: "badDebtTransactionCategory",
                indent: true,
              },
            ],
          },
        ],
      },
    };
  },
  mixins: [],
  computed: {
    ...mapGetters(["currentLoading"]),
    dataReady() {
      return this.propertySettingsData && this.propertySettingsData.length > 0;
    },
    filterBtnText() {
      return this.canFilter ? "Apply" : "Applied";
    },
    canFilter() {
      return (
        this.dirty &&
        this.selectedProperties.length > 0 &&
        this.selectedYearOption &&
        this.selectedUseOption
      );
    },
    canExport() {
      return !this.dirty && this.dataReady;
    },
    groupHeaderStyle() {
      var width = 250 * ((this.propertySettingsData || []).length + 1) + "px";
      return { maxWidth: width, minWidth: width };
    },
    nonPropertyGroupProperties() {
      return this.properties
        .filter((p) => !p.isPropertyGroup && p.header == null)
        .map((p) => p);
    },
    areAllPropertiesSelected() {
      return (
        this.properties.length > 0 &&
        this.selectedProperties.length ===
          this.nonPropertyGroupProperties.length
      );
    },
  },
  methods: {
    ...mapActions(["changeLoading", "activateErrorDialog"]),
    async loadProperties() {
      this.changeLoading(true);
      var properties = await propertyService.getDropdown(true);
      this.properties = [].concat((properties && properties.data) || []);
      this.changeLoading(false);
    },
    async loadExistingBudgetYears() {
      this.changeLoading(true);
      var years = await budgetService.getExistingBudgetYears();
      this.yearOptions = (years || []).map((y) => {
        return { text: y, value: +y };
      });
      this.changeLoading(false);
    },
    toggleSelectAllProperties() {
      this.dirty = true;
      if (this.areAllPropertiesSelected) {
        this.allPropertiesSelected = false;
        this.selectedProperties = [];
      } else {
        this.allPropertiesSelected = true;
        this.selectedProperties = this.nonPropertyGroupProperties;
      }
    },
    returnHeadersAndFilter(item, queryText, itemText) {
      if (item.header) {
        return true;
      }
      return (
        itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      );
    },
    async apply() {
      this.dirty = false;
      this.changeLoading(true);
      var selectedPropertyIds = (this.selectedProperties || []).map((p) => {
        return p.value;
      });
      var budgetSettings = await budgetService.getBudgetSettingsByPropertyIds(
        selectedPropertyIds,
        this.selectedYearOption,
        this.selectedUseOption
      );

      this.propertySettingsData = budgetSettings || [];

      this.changeLoading(false);
    },
    dirtify() {
      this.dirty = true;
    },
    async doExport() {
      var selectedPropertyIds = (this.selectedProperties || []).map((p) => {
        return p.value;
      });
      var dl = await budgetService.getSettingsSummaryExportFile(
        {
          propertyIds: selectedPropertyIds,
          year: this.selectedYearOption,
          useOption: this.selectedUseOption,
        },
        this.propertySettingsData || []
      );

      var blob = new Blob([dl.data]);
      var dlUrl = URL.createObjectURL(blob);

      var a = document.createElement("a");
      a.href = dlUrl;
      a.download = "SettingsSummary.xlsx";
      document.body.appendChild(a);

      a.click();
    },
    clear() {
      this.selectedProperties = [];
      this.allPropertiesSelected = false;
      this.selectedYearOption = 0;
      this.selectedUseOption = 1;

      this.propertySettingsData = [];
      this.colDefs = [];
      this.dirty = false;
    },
    toggleSection(section) {
      Vue.set(section, "hide", !section.hide);
    },
    getVisibleFieldsForSection(section) {
      return section.fields.filter((field) => {
        return !field.hide;
      });
    },
    getMaxLengthArrayForArrayField(field) {
      var maxLength = 0;
      (this.propertySettingsData || []).forEach((propertySettings) => {
        if (propertySettings[field].length > maxLength) {
          maxLength = propertySettings[field].length;
        }
      });
      var maxLengthArray = [];
      for (var i = 0; i < maxLength; i++) {
        maxLengthArray.push("");
      }
      return maxLengthArray;
    },
  },
  async mounted() {
    await this.loadProperties();
    await this.loadExistingBudgetYears();
  },
  watch: {
    selectedProperties: function () {
      // remove partial values from search strings
      var listProperties = this.selectedProperties.filter((pr) => pr.value);
      if (listProperties.length != this.selectedProperties.length) {
        this.selectedProperties = listProperties;
      }

      this.allPropertiesSelected = this.areAllPropertiesSelected;
    },
  },
};
</script>

<style lang="scss" scoped>
.bordered-row {
  border: 1px solid grey;
  white-space: nowrap;
}

.scrollable {
  overflow-x: auto;
}

.group-header {
  background-color: var(--v-resBlue-darken1);
  color: white;
  padding: 10px;
  cursor: pointer;
}

.icon {
  color: white;
}

table {
  border-collapse: collapse;
}

td {
  min-width: 250px;
  max-width: 250px;
  text-align: center;
  padding: 16px;
  background-color: var(--v-resGrey-lighten4);
  border: 1px solid var(--v-resGrey-lighten1);
}

td.fieldName {
  text-align: left;
}
.indentation {
  padding-left: 30px;
}
.bold {
  font-weight: bold;
}
</style>
